import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import BookingModal from '../components/BookingModal';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001';

const Tours = () => {
    const [bookingModalOpen, setBookingModalOpen] = useState(false);
    const [selectedTourName, setSelectedTourName] = useState('');
    const [selectedTourPrice, setSelectedTourPrice] = useState('');
    const [selectedTourId, setSelectedTourId] = useState(null);
    const [tours, setTours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [filters, setFilters] = useState({
        country: 'all',
        category: 'all'
    });
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [filterOptions, setFilterOptions] = useState({
        countries: [],
        categories: []
    });
    const [searchParams] = useSearchParams();

    // Helper to get category name safely
    const getCategoryName = (category) => {
        if (!category) return '';
        return typeof category === 'object' ? category.name : category;
    };

    const openBookingModal = (tourName, price, tourId = null) => {
        setSelectedTourName(tourName);
        setSelectedTourPrice(price || '');
        setSelectedTourId(tourId);
        setBookingModalOpen(true);
    };

    // Fetch available filters
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [catsRes, filtersRes] = await Promise.all([
                    axios.get('/api/categories'),
                    axios.get('/api/tours/filters')
                ]);
                setFilterOptions({
                    categories: catsRes.data,
                    countries: filtersRes.data.countries
                });
            } catch (err) {
                console.error("Error fetching filters", err);
            }
        };
        fetchFilters();
    }, []);

    // Fetch tours
    const fetchTours = async (pageNum, currentFilters) => {
        try {
            const params = {
                page: pageNum,
                limit: 12
            };

            if (currentFilters.country !== 'all') params.country = currentFilters.country;
            if (currentFilters.category !== 'all') params.category = currentFilters.category;

            const response = await axios.get('/api/tours', { params });
            const { tours: newTours, pagination } = response.data;

            if (pageNum === 1) {
                setTours(newTours);
            } else {
                setTours(prev => [...prev, ...newTours]);
            }

            setHasMore(pagination.page < pagination.pages);
        } catch (error) {
            console.error('Error fetching tours:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Initial fetch — reads ?category= from URL if present
    useEffect(() => {
        const catFromUrl = searchParams.get('category');
        if (catFromUrl) {
            const newFilters = { ...filters, category: catFromUrl };
            setFilters(newFilters);
            fetchTours(1, newFilters);
        } else {
            fetchTours(1, filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle Load More
    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        setLoadingMore(true);
        fetchTours(nextPage, filters);
    };

    const handleFilterChange = (filterType, value) => {
        const newFilters = { ...filters, [filterType]: value };
        setFilters(newFilters);
        setPage(1);
        setLoading(true);
        // Reset tours immediately to show loading state cleanly or just update
        setTours([]);
        fetchTours(1, newFilters);
    };



    const resetFilters = () => {
        setFilters({
            country: 'all',
            category: 'all'
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(price);
    };

    const getTourImage = (tour) => {
        if (tour.images && tour.images.length > 0) {
            const img = tour.images[0];
            // If it's a relative path (starts with /), prefix with API URL
            if (img.startsWith('/')) {
                return `${API_URL}${img}`;
            }
            return img;
        }
        return 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop';
    };



    const getBadge = (tour) => {
        if (tour.isFeatured && tour.tags?.includes('bestseller')) return 'BESTSELLER';
        if (tour.tags?.includes('hot deal')) return 'HOT DEAL';
        if (getCategoryName(tour.category) === 'luxury') return 'LUXURY';
        return null;
    };



    return (
        <>
            <div>
                <Navigation />

                {/* Hero Section */}
                <section className="relative mt-20 overflow-hidden bg-gradient-to-r from-brand-blue/80 to-brand-teal/60 text-white py-24">
                    <div className="blob bg-brand-green w-96 h-96 top-20 left-10"></div>
                    <div className="blob bg-brand-teal w-80 h-80 bottom-20 right-20" style={{ animationDelay: '2s' }}></div>

                    <div className="absolute inset-0">
                        <img src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&h=600&fit=crop" alt="Tours" className="w-full h-full object-cover opacity-20" />
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">Explore All Tours</h1>
                        <p className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto">Discover amazing destinations worldwide with our curated travel experiences</p>
                    </div>
                </section>

                {/* Filter Section */}
                <section className="py-8 bg-white shadow-md">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                                <div className="flex-1 md:w-64">
                                    <label className="block text-gray-700 font-semibold mb-2 text-sm">Filter by Country</label>
                                    <select
                                        value={filters.country}
                                        onChange={(e) => handleFilterChange('country', e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                                    >
                                        <option value="all">All Countries</option>
                                        {filterOptions.countries.map(country => (
                                            <option key={country} value={country}>
                                                {country.charAt(0).toUpperCase() + country.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex-1 md:w-64">
                                    <label className="block text-gray-700 font-semibold mb-2 text-sm">Filter by Category</label>
                                    <select
                                        value={filters.category}
                                        onChange={(e) => handleFilterChange('category', e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                                    >
                                        <option value="all">All Categories</option>
                                        {filterOptions.categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <button
                                    onClick={resetFilters}
                                    className="px-6 py-3 border-2 border-brand-teal text-brand-teal rounded-lg font-semibold hover:bg-brand-teal hover:text-white transition transform hover:scale-105"
                                >
                                    <i className="fas fa-redo mr-2"></i>Reset
                                </button>
                                <div className="text-gray-600 font-medium">

                                    <span id="tourCount">{tours.length}</span> Tours
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Tours Grid Section */}
                <section className="py-20 bg-gray-50 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5">
                        <div className="blob bg-brand-green w-96 h-96 top-40 left-40"></div>
                        <div className="blob bg-brand-cyan w-80 h-80 bottom-40 right-40" style={{ animationDelay: '3s' }}></div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        {loading && tours.length === 0 ? (
                            <div className="text-center py-12">
                                <i className="fas fa-spinner fa-spin text-4xl text-brand-teal mb-4"></i>
                                <p className="text-gray-600">Loading tours...</p>
                            </div>
                        ) : tours.length === 0 ? (
                            <div id="noResults" className="text-center py-20">
                                <i className="fas fa-search text-6xl text-gray-300 mb-6"></i>
                                <h3 className="text-3xl font-bold text-gray-800 mb-4">No Tours Found</h3>
                                <p className="text-gray-600 text-lg mb-8">Try adjusting your filters to see more results</p>
                                <button
                                    onClick={resetFilters}
                                    className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition transform hover:scale-110"
                                >
                                    Reset Filters
                                </button>
                            </div>
                        ) : (
                            <>
                                <div id="toursGrid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                                    {tours.map((tour) => {
                                        const badge = getBadge(tour);
                                        return (
                                            <div key={tour.id || tour._id} className="tour-card bg-white rounded-2xl overflow-hidden shadow-lg hover-lift card-shine">
                                                <div className="relative h-64 overflow-hidden hover-zoom">
                                                    <img src={getTourImage(tour)} alt={tour.title} className="w-full h-full object-cover transition duration-700" />
                                                    {badge && (
                                                        <div className={`absolute top-4 left-4 ${badge === 'HOT DEAL' ? 'bg-red-500' : badge === 'LUXURY' ? 'bg-gradient-to-r from-brand-green to-brand-teal' : 'bg-brand-cyan'} text-white px-4 py-2 rounded-full font-semibold text-sm wave-animation`}>
                                                            {badge}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="p-6">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className="text-sm text-gray-500">
                                                            <i className="fas fa-clock mr-2"></i>
                                                            {(tour.duration?.days ?? 0)} Days / {(tour.duration?.nights ?? 0)} Nights
                                                        </span>

                                                    </div>
                                                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{tour.title || 'Untitled Tour'}</h3>
                                                    <p className="text-gray-600 mb-4">
                                                        {tour.description
                                                            ? `${tour.description.substring(0, 100)}${tour.description.length > 100 ? '...' : ''}`
                                                            : 'No description available.'}
                                                    </p>
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                                                            <i className="fas fa-map-marker-alt"></i>
                                                            <span>
                                                                {[tour.destination, tour.country ? tour.country.charAt(0).toUpperCase() + tour.country.slice(1) : '']
                                                                    .filter(Boolean)
                                                                    .join(', ') || 'Location not specified'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 mb-4">
                                                        <div>
                                                            <span className="text-gray-500 text-sm">From</span>
                                                            <div className="text-3xl font-bold bg-gradient-to-r from-brand-teal to-brand-cyan bg-clip-text text-transparent">{formatPrice(tour.price)}</div>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-3">
                                                        <Link
                                                            to={`/tours/${tour.id || tour._id}`}
                                                            className="flex-1 text-center bg-white border-2 border-brand-teal text-brand-teal px-4 py-3 rounded-full font-semibold hover:bg-brand-teal hover:text-white transition transform hover:scale-105"
                                                        >
                                                            <i className="fas fa-info-circle mr-2"></i>View Details
                                                        </Link>
                                                        <button
                                                            onClick={() => openBookingModal(tour.title, formatPrice(tour.price), tour.id || tour._id)}
                                                            className="flex-1 bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-4 py-3 rounded-full font-semibold hover:shadow-lg transition transform hover:scale-105"
                                                        >
                                                            Book Now
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {hasMore && (
                                    <div className="text-center mt-12">
                                        <button
                                            onClick={handleLoadMore}
                                            disabled={loadingMore}
                                            className="bg-brand-teal text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transition transform hover:scale-105 disabled:opacity-50"
                                        >
                                            {loadingMore ? (
                                                <><i className="fas fa-spinner fa-spin mr-2"></i>Loading...</>
                                            ) : (
                                                'Load More Tours'
                                            )}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>

                <Footer />

                <BookingModal
                    isOpen={bookingModalOpen}
                    onClose={() => setBookingModalOpen(false)}
                    tourName={selectedTourName}
                    tourPrice={selectedTourPrice}
                    tourId={selectedTourId}
                />
            </div>
        </>
    );
};

export default Tours;
